import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import EnhancedTable from "@meetyoo/components/src/components/Table/EnhancedTable";
import TableLayout from "../TableLayout";
import InfoModal from "../Modal/InfoModal";
import { initialSearchFilter, defaultTableLayout } from "../../../../reducers/projectmail";
import { Filter } from "../Filter";
import TagModal from "../Modal/TagModal";

import { getSearchedTickets, getAvailableTags, setTableLayout, setRowAmount } from "../../../../actions/projectmail";
import TableConfigurator from "../TableLayout/TableConfigurator";
import TableSelector from "../TableSelector";
import EmailModal from "../Modal/EmailModal/EmailModal";

const filterTickets = (tickets, filter) => {
  const ret = tickets.filter(ticket =>
    Object.entries(filter)
      .map(([filterKey, filterValue]) => {
        const noDefaultValue = ticket[filterKey] !== undefined && ticket[filterKey] !== -1 && ticket[filterKey] !== "-";
        if (noDefaultValue && filterKey === "eventStartTime" && filterValue !== initialSearchFilter[filterKey]) {
          return ticket[filterKey] > filterValue;
        }
        if (noDefaultValue && filterKey === "eventEndTime" && filterValue !== initialSearchFilter[filterKey]) {
          return ticket[filterKey] < filterValue;
        }
        if (noDefaultValue && filterValue !== null) {
          return ticket[filterKey]
            .toString()
            .toLowerCase()
            .includes(filterValue.toString().toLowerCase());
        }
        if (filterValue !== initialSearchFilter[filterKey] && !noDefaultValue && ticket[filterKey] !== undefined) {
          return false;
        }
        return true;
      })
      .every(includeBoolean => includeBoolean === true)
  );
  return ret;
};

export const TableComponent = ({
  stateTickets,
  getTicketsFn,
  filter,
  title,
  type,
  translate,
  tableLayout,
  setInitialTableLayoutFn,
  setRowAmountFn,
  rowAmount,
}) => {
  const table = useRef();
  const filterRef = useRef();
  const [ownTickets, setOwnTickets] = useState(stateTickets[type] ? [...stateTickets[type]] : []);
  const [ownFilter, setOwnFilter] = useState(filter);
  useEffect(() => {
    if (ownFilter !== undefined && filter && stateTickets[type]) {
      const newTickets = filterTickets(stateTickets[type], filter);
      setOwnTickets(newTickets);
    }
    setOwnFilter(filter);
  }, [ownFilter, setOwnFilter, filter, stateTickets, type]);

  const [openFilter, setOpenFilter] = useState(filter ? filter.changed : false);
  const [openSettings, setOpenSettings] = useState(false);
  useEffect(() => {
    if (tableLayout === undefined) {
      setInitialTableLayoutFn(type);
    }
  }, [setInitialTableLayoutFn, tableLayout, type]);

  const createHeadRow = () => {
    return tableLayout.map(tItem => ({
      id: tItem,
      label: translate[TableConfigurator[tItem].label],
    }));
  };
  // request for tickets
  useEffect(() => {
    const request = async () => {
      if (stateTickets === undefined) {
        await getTicketsFn(initialSearchFilter, type);
      } else {
        if (stateTickets[type] === undefined) {
          await getTicketsFn(initialSearchFilter, type);
        }
        if (ownTickets === undefined && stateTickets[type]) {
          setOwnTickets(stateTickets[type].filter(ticket => ticket));
        }
      }
    };
    request();
  }, [ownTickets, getTicketsFn, stateTickets, setOwnTickets, filter, type]);

  return (
    ownTickets &&
    tableLayout !== undefined && [
      <div key="table">
        <EnhancedTable
          rowsPerPageState={rowAmount}
          setReturnRowsPerPage={amount => {
            setRowAmountFn(amount);
            table.current.resetPage();
          }}
          ref={table}
          title={title}
          rows={ownTickets}
          headRows={createHeadRow()}
          tableRowComponent={TableLayout}
          rowProps={{
            setOpenFilter: boolean => {
              setOpenFilter(boolean);
            },
            setFilter: (filterValue, filterKey) => filterRef.current.setFilter(filterValue, filterKey),
            type,
          }}
          deleteButton={false}
          selectAll={false}
          openFilter={openFilter}
          filterChildren={
            <Filter
              ref={filterRef}
              type={type}
              resetPage={() => table.current.resetPage()}
              openFilter={openFilter}
              closeFilter={() => setOpenFilter(false)}
            />
          }
          callBackExpand={event => {
            setOpenFilter(event);
          }}
          stateSortBy="sortTimeStamp"
          oderState="desc"
          settingsCallback={() => setOpenSettings(!openSettings)}
        />
      </div>,
      <InfoModal key="modal" />,
      <EmailModal key="tableModal" />,
      <TableSelector type={type} open={openSettings} onClose={() => setOpenSettings(false)} key="settings" />,
      <TagModal key="tag-modal" />,
    ]
  );
};

TableComponent.defaultProps = {
  stateTickets: undefined,
  filter: undefined,
  tableLayout: undefined,
};

TableComponent.propTypes = {
  stateTickets: PropTypes.object,
  title: PropTypes.string.isRequired,
  getTicketsFn: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  filter: PropTypes.object,
  translate: PropTypes.object.isRequired,
  tableLayout: PropTypes.array,
  setInitialTableLayoutFn: PropTypes.func.isRequired,
  setRowAmountFn: PropTypes.func.isRequired,
  rowAmount: PropTypes.number.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  stateTickets: state.projectmail.tickets,
  filter: state.projectmail.searchFilter[ownProps.type],
  tags: state.projectmail.tags,
  colorMap: state.projectmail.colorMap,
  translate: state.ui.translate,
  tableLayout: state.projectmail.tableLayout[ownProps.type],
  rowAmount: state.projectmail.rowAmount,
});

const mapDispatchToProps = dispatch => ({
  getTicketsFn: async (filter, type, page) => {
    await dispatch(getSearchedTickets(filter, type, page));
  },
  getAvailableTagsFn: async () => {
    await dispatch(getAvailableTags());
  },
  setInitialTableLayoutFn: type => dispatch(setTableLayout(defaultTableLayout, type)),
  setRowAmountFn: amount => dispatch(setRowAmount(amount)),
});

const Table = connect(
  mapStateToProps,
  mapDispatchToProps
)(TableComponent);

export default Table;
